import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Calendar } from '../../components/ui/calendar';
import { formatDateTime, formatTime } from '../../lib/utils';
import { Plus, FileText, Search, Car, DollarSign, Moon, Download, Eye, CalendarIcon, Clock, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import ShiftNoteFormDialog from './ShiftNoteFormDialog';

const ShiftNotesPage = ({ readOnly = false }) => {
  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const { api, user, isFamily } = useAuth();
  const [notes, setNotes] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [participantFilter, setParticipantFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  

  // PDF Export state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    participant_id: '',
    start_date: '',
    end_date: '',
    include_incidents: false
  });
  
  const canExport = user?.role === 'admin' || user?.role === 'house_manager';
  

  const [editingNote, setEditingNote] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, [participantFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = participantFilter !== 'all' ? { participant_id: participantFilter } : {};
      const [notesRes, shiftsRes, participantsRes] = await Promise.all([
        api.get('/shift-notes', { params }),
        !isFamily ? api.get('/shifts', { params: { status: 'accepted,completed,in_progress,offered,scheduled', ...(user?.role === 'worker' ? { worker_id: user.id } : {}) } }) : Promise.resolve({ data: [] }),
        api.get('/participants')
      ]);
      setNotes(notesRes.data);
      setShifts(shiftsRes.data);
      setParticipants(participantsRes.data);
    } catch (error) {
      toast.error('Failed to load shift notes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (noteData, asDraft = false) => {
    if (!noteData.shift_id || !noteData.participant_id) {
      toast.error('Please select a shift and participant');
      return;
    }
    try {
      await api.post('/shift-notes', { ...noteData, status: asDraft ? 'draft' : 'submitted' });
      toast.success(asDraft ? 'Draft saved' : 'Shift note submitted');
      setCreateDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to create shift note');
    }
  };

  const handleEditDraft = (note) => {
    setEditingNote({
      id: note.id,
      shift_id: note.shift_id,
      participant_id: note.participant_id,
      adls: note.adls || { showering: false, dressing: false, toileting: false, notes: '' },
      meals: note.meals || { breakfast: false, lunch: false, dinner: false, notes: '' },
      medications: note.medications || { administered: false, notes: '' },
      behaviours: note.behaviours || '',
      community_access: note.community_access || '',
      incidents_near_misses: note.incidents_near_misses || '',
      goals_progress: note.goals_progress || '',
      general_notes: note.general_notes || '',
      is_family_safe: note.is_family_safe !== false,
      travel: note.travel || { kilometers: '', purpose: '', start_location: '', end_location: '' },
      expenses: note.expenses || [],
      active_overnight: note.active_overnight || { hours: '', reason: '' }
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (submit = false) => {
    if (!editingNote) return;
    try {
      await api.put('/shift-notes/' + editingNote.id, editingNote);
      if (submit) {
        await api.put('/shift-notes/' + editingNote.id + '/submit');
        toast.success('Shift note submitted');
      } else {
        toast.success('Draft updated');
      }
      setEditDialogOpen(false);
      setEditingNote(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save shift note');
    }
  };

  const handleSubmitDraftFromTable = async (noteId) => {
    try {
      await api.put('/shift-notes/' + noteId + '/submit');
      toast.success('Shift note submitted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    }
  };





  const sortedShifts = useMemo(() => {
    return [...shifts].sort((a, b) => new Date(b.start_time) - new Date(a.start_time));
  }, [shifts]);

  const filteredNotes = useMemo(() => notes.filter(n => 
    n.participant_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.general_notes?.toLowerCase().includes(searchQuery.toLowerCase())
  ), [notes, searchQuery]);

  // Handle view note click
  const handleViewNote = (note) => {
    setSelectedNote(note);
    setViewDialogOpen(true);
  };

  const columns = useMemo(() => [
    {
      header: 'Date/Time',
      cell: (row) => formatDateTime(row.created_at)
    },
    {
      header: 'Participant',
      accessor: 'participant_name'
    },
    {
      header: 'Worker',
      accessor: 'worker_name'
    },
    {
      header: 'ADLs',
      cell: (row) => {
        const adls = row.adls || {};
        const completed = [adls.showering, adls.dressing, adls.toileting].filter(Boolean).length;
        return `${completed}/3`;
      }
    },
    {
      header: 'Travel',
      cell: (row) => row.travel?.kilometers ? `${row.travel.kilometers} km` : '—'
    },
    {
      header: 'Expenses',
      cell: (row) => {
        const total = row.expenses?.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);
        return total > 0 ? `$${total.toFixed(2)}` : '—';
      }
    },
    {
      header: 'Active O/N',
      cell: (row) => row.active_overnight?.hours ? `${row.active_overnight.hours}h` : '—'
    },
    {
      header: 'Notes',
      cell: (row) => (
        <span className="text-sm text-muted-foreground truncate max-w-[150px] block">
          {row.general_notes || '—'}
        </span>
      )
    },
    {
      header: 'Status',
      cell: (row) => (
        <span className={`text-xs font-medium px-2 py-1 rounded ${
          row.status === 'draft' ? 'bg-amber-500/20 text-amber-600' : 'bg-green-500/20 text-green-600'
        }`}>
          {row.status === 'draft' ? 'Draft' : 'Submitted'}
        </span>
      )
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-1">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => handleViewNote(row)}
            data-testid={`view-note-${row.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === 'draft' && row.worker_id === user?.id && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => handleEditDraft(row)}
              data-testid={`edit-note-${row.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      )
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [user?.id]);

  // PDF Export function
  const handleExportPDF = async () => {
    if (!exportOptions.participant_id || !exportOptions.start_date || !exportOptions.end_date) {
      toast.error('Please select participant and date range');
      return;
    }

    setExportLoading(true);
    try {
      const response = await api.post('/export/shift-notes-pdf', exportOptions, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const participant = participants.find(p => p.id === exportOptions.participant_id);
      link.setAttribute('download', `shift_notes_${participant?.last_name || 'participant'}_${exportOptions.start_date}_${exportOptions.end_date}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF exported successfully');
      setExportDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to export PDF');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="shift-notes-page">
      <PageHeader 
        title="Shift Notes"
        description={readOnly ? "View shift notes for your participant" : "Document participant care and activities"}
        actions={
          <div className="flex gap-2">
            {canExport && (
              <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline">
                    <Download className="h-4 w-4 mr-2" />
                    Export PDF
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Export Shift Notes PDF</DialogTitle>
                    <DialogDescription>
                      Generate a PDF report of shift notes for a participant
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>Participant *</Label>
                      <Select 
                        value={exportOptions.participant_id} 
                        onValueChange={(v) => setExportOptions({...exportOptions, participant_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select participant" />
                        </SelectTrigger>
                        <SelectContent>
                          {participants.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.first_name} {p.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input 
                          type="date"
                          value={exportOptions.start_date}
                          onChange={(e) => setExportOptions({...exportOptions, start_date: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date *</Label>
                        <Input 
                          type="date"
                          value={exportOptions.end_date}
                          onChange={(e) => setExportOptions({...exportOptions, end_date: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox 
                        id="include-incidents"
                        checked={exportOptions.include_incidents}
                        onCheckedChange={(checked) => setExportOptions({...exportOptions, include_incidents: checked})}
                      />
                      <Label htmlFor="include-incidents" className="cursor-pointer">
                        Include linked incidents
                      </Label>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button variant="outline" onClick={() => handleExport('csv')} disabled={exportLoading}>
                      {exportLoading ? 'Generating...' : 'CSV'}
                    </Button>
                    <Button onClick={() => handleExport('pdf')} disabled={exportLoading}>
                      {exportLoading ? 'Generating...' : 'PDF'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
            {!readOnly && (
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="create-note-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
              </DialogTrigger>
              <ShiftNoteFormDialog
                shifts={shifts}
                participants={participants}
                onSave={handleCreate}
                onClose={() => setCreateDialogOpen(false)}
              />

            </Dialog>
            )}
          </div>
        }
      />

      {/* Filters */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Participant</Label>
              <Select value={participantFilter} onValueChange={setParticipantFilter}>
                <SelectTrigger className="w-48" data-testid="notes-participant-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Participants</SelectItem>
                  {participants.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.first_name} {p.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1 flex-1 max-w-md">
              <Label className="text-xs text-muted-foreground">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Search notes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="notes-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <DataTable 
        columns={columns}
        data={filteredNotes}
        emptyMessage="No shift notes found"
      />

      {/* Edit Draft Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Draft Shift Note</DialogTitle>
          </DialogHeader>
          {editingNote && (
            <div className="space-y-6 py-4">
              <div className="space-y-3 p-4 rounded-lg bg-muted/30">
                <Label className="text-base font-semibold">Activities of Daily Living (ADLs)</Label>
                <div className="flex flex-wrap gap-4">
                  {['showering', 'dressing', 'toileting'].map(field => (
                    <div key={field} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`edit-${field}`}
                        checked={editingNote.adls?.[field] || false}
                        onCheckedChange={(c) => setEditingNote({...editingNote, adls: {...editingNote.adls, [field]: c}})}
                      />
                      <label htmlFor={`edit-${field}`} className="text-sm capitalize">{field}</label>
                    </div>
                  ))}
                </div>
                <Input placeholder="ADL notes..." value={editingNote.adls?.notes || ''} onChange={(e) => setEditingNote({...editingNote, adls: {...editingNote.adls, notes: e.target.value}})} />
              </div>
              <div className="space-y-3 p-4 rounded-lg bg-muted/30">
                <Label className="text-base font-semibold">Meals</Label>
                <div className="flex flex-wrap gap-4">
                  {['breakfast', 'lunch', 'dinner'].map(field => (
                    <div key={field} className="flex items-center space-x-2">
                      <Checkbox 
                        id={`edit-${field}`}
                        checked={editingNote.meals?.[field] || false}
                        onCheckedChange={(c) => setEditingNote({...editingNote, meals: {...editingNote.meals, [field]: c}})}
                      />
                      <label htmlFor={`edit-${field}`} className="text-sm capitalize">{field}</label>
                    </div>
                  ))}
                </div>
                <Input placeholder="Meal notes..." value={editingNote.meals?.notes || ''} onChange={(e) => setEditingNote({...editingNote, meals: {...editingNote.meals, notes: e.target.value}})} />
              </div>
              <div className="space-y-3 p-4 rounded-lg bg-muted/30">
                <Label className="text-base font-semibold">Medications</Label>
                <div className="flex items-center space-x-2">
                  <Checkbox id="edit-meds" checked={editingNote.medications?.administered || false} onCheckedChange={(c) => setEditingNote({...editingNote, medications: {...editingNote.medications, administered: c}})} />
                  <label htmlFor="edit-meds" className="text-sm">Medications Administered</label>
                </div>
                <Input placeholder="Medication notes..." value={editingNote.medications?.notes || ''} onChange={(e) => setEditingNote({...editingNote, medications: {...editingNote.medications, notes: e.target.value}})} />
              </div>
              <div className="space-y-2">
                <Label>Behaviours Observed</Label>
                <Textarea value={editingNote.behaviours || ''} onChange={(e) => setEditingNote({...editingNote, behaviours: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Community Access</Label>
                <Textarea value={editingNote.community_access || ''} onChange={(e) => setEditingNote({...editingNote, community_access: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Goals Progress</Label>
                <Textarea value={editingNote.goals_progress || ''} onChange={(e) => setEditingNote({...editingNote, goals_progress: e.target.value})} rows={2} />
              </div>
              <div className="space-y-2">
                <Label>General Notes</Label>
                <Textarea value={editingNote.general_notes || ''} onChange={(e) => setEditingNote({...editingNote, general_notes: e.target.value})} rows={3} />
              </div>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setEditDialogOpen(false); setEditingNote(null); }}>Cancel</Button>
            <Button variant="secondary" onClick={() => handleSaveEdit(false)} data-testid="update-draft-btn">Save Draft</Button>
            <Button onClick={() => handleSaveEdit(true)} data-testid="submit-draft-btn">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Note Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Shift Note Details
            </DialogTitle>
            <DialogDescription>
              {selectedNote?.participant_name} - {formatDateTime(selectedNote?.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-muted/30">
                <div>
                  <Label className="text-xs text-muted-foreground">Worker</Label>
                  <p className="font-medium">{selectedNote.worker_name || '—'}</p>
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Shift</Label>
                  <p className="font-medium">{selectedNote.shift_type || '—'}</p>
                </div>
              </div>

              {/* ADLs */}
              {selectedNote.adls && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-3 block">Activities of Daily Living (ADLs)</Label>
                  <div className="flex flex-wrap gap-3 mb-2">
                    <span className={`px-2 py-1 rounded text-sm ${selectedNote.adls.showering ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      Showering: {selectedNote.adls.showering ? '✓' : '✗'}
                    </span>
                    <span className={`px-2 py-1 rounded text-sm ${selectedNote.adls.dressing ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      Dressing: {selectedNote.adls.dressing ? '✓' : '✗'}
                    </span>
                    <span className={`px-2 py-1 rounded text-sm ${selectedNote.adls.toileting ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      Toileting: {selectedNote.adls.toileting ? '✓' : '✗'}
                    </span>
                  </div>
                  {selectedNote.adls.notes && (
                    <p className="text-sm text-muted-foreground mt-2">{selectedNote.adls.notes}</p>
                  )}
                </div>
              )}

              {/* Meals */}
              {selectedNote.meals && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-3 block">Meals</Label>
                  <div className="flex flex-wrap gap-3 mb-2">
                    <span className={`px-2 py-1 rounded text-sm ${selectedNote.meals.breakfast ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      Breakfast: {selectedNote.meals.breakfast ? '✓' : '✗'}
                    </span>
                    <span className={`px-2 py-1 rounded text-sm ${selectedNote.meals.lunch ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      Lunch: {selectedNote.meals.lunch ? '✓' : '✗'}
                    </span>
                    <span className={`px-2 py-1 rounded text-sm ${selectedNote.meals.dinner ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                      Dinner: {selectedNote.meals.dinner ? '✓' : '✗'}
                    </span>
                  </div>
                  {selectedNote.meals.notes && (
                    <p className="text-sm text-muted-foreground mt-2">{selectedNote.meals.notes}</p>
                  )}
                </div>
              )}

              {/* Medications */}
              {selectedNote.medications && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-3 block">Medications</Label>
                  <span className={`px-2 py-1 rounded text-sm ${selectedNote.medications.administered ? 'bg-green-500/20 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                    {selectedNote.medications.administered ? '✓ Administered' : '✗ Not Administered'}
                  </span>
                  {selectedNote.medications.notes && (
                    <p className="text-sm text-muted-foreground mt-2">{selectedNote.medications.notes}</p>
                  )}
                </div>
              )}

              {/* Behaviours */}
              {selectedNote.behaviours && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-2 block">Behaviours Observed</Label>
                  <p className="text-sm whitespace-pre-wrap">{selectedNote.behaviours}</p>
                </div>
              )}

              {/* Community Access */}
              {selectedNote.community_access && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-2 block">Community Access</Label>
                  <p className="text-sm whitespace-pre-wrap">{selectedNote.community_access}</p>
                </div>
              )}

              {/* Goals Progress */}
              {selectedNote.goals_progress && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-2 block">Goals Progress</Label>
                  <p className="text-sm whitespace-pre-wrap">{selectedNote.goals_progress}</p>
                </div>
              )}

              {/* Travel */}
              {selectedNote.travel?.kilometers && (
                <div className="p-4 rounded-lg bg-blue-500/5 border border-blue-500/20">
                  <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                    <Car className="h-4 w-4 text-blue-500" />
                    Travel
                  </Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Kilometers:</span>
                      <p className="font-medium">{selectedNote.travel.kilometers} km</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Purpose:</span>
                      <p className="font-medium capitalize">{selectedNote.travel.purpose?.replace('_', ' ') || '—'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Start:</span>
                      <p className="font-medium">{selectedNote.travel.start_location || '—'}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">End:</span>
                      <p className="font-medium">{selectedNote.travel.end_location || '—'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Expenses */}
              {selectedNote.expenses && selectedNote.expenses.length > 0 && (
                <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                  <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-green-500" />
                    Expenses
                  </Label>
                  <div className="space-y-2">
                    {selectedNote.expenses.map((expense, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 rounded bg-background/50 border border-border/40">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{expense.description}</span>
                          {expense.receipt && (
                            <span className="text-xs bg-green-500/10 text-green-600 px-2 py-0.5 rounded">Receipt</span>
                          )}
                        </div>
                        <span className="font-medium">${parseFloat(expense.amount || 0).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="text-right font-semibold mt-2">
                      Total: ${selectedNote.expenses.reduce((sum, e) => sum + parseFloat(e.amount || 0), 0).toFixed(2)}
                    </div>
                  </div>
                </div>
              )}

              {/* Active Overnight Hours */}
              {selectedNote.active_overnight?.hours && (
                <div className="p-4 rounded-lg bg-indigo-500/5 border border-indigo-500/20">
                  <Label className="text-base font-semibold mb-3 flex items-center gap-2">
                    <Moon className="h-4 w-4 text-indigo-500" />
                    Active Overnight Hours
                  </Label>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Hours Active:</span>
                      <p className="font-medium">{selectedNote.active_overnight.hours}h</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Reason:</span>
                      <p className="font-medium capitalize">{selectedNote.active_overnight.reason?.replace('_', ' ') || '—'}</p>
                    </div>
                  </div>
                  {selectedNote.active_overnight.notes && (
                    <p className="text-sm text-muted-foreground mt-2">{selectedNote.active_overnight.notes}</p>
                  )}
                </div>
              )}

              {/* General Notes */}
              {selectedNote.general_notes && (
                <div className="p-4 rounded-lg bg-muted/30">
                  <Label className="text-base font-semibold mb-2 block">General Notes</Label>
                  <p className="text-sm whitespace-pre-wrap">{selectedNote.general_notes}</p>
                </div>
              )}

              {/* Incidents/Near Misses */}
              {selectedNote.incidents_near_misses && (
                <div className="p-4 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <Label className="text-base font-semibold mb-2 flex items-center gap-2 text-amber-600">
                    Incidents / Near Misses
                  </Label>
                  <p className="text-sm whitespace-pre-wrap">{selectedNote.incidents_near_misses}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftNotesPage;
